/**
 * BriefingView — the conversational Heute morning briefing (Story UI-U10, AC2/AC3).
 *
 * Ports the structure of Sebastian's `heute-v2-view.tsx` to OUR primitives + the
 * live Brok stack:
 *   1. Greeting + a `decisionSentence` grounded in the feature-detected briefing counts.
 *   2. Four Entscheid tiles (BriefingDecisionTiles).
 *   3. KI-Empfehlungen — a DOCUMENTED placeholder (mock in mock-mode, honest empty
 *      line in live-mode; no recommendations endpoint exists).
 *   4. Portfolio-mini KPIs — same placeholder stance (no KPI endpoint).
 * Each section arrives with a once-per-session entrance stagger, GATED by
 * prefers-reduced-motion (and jsdom's missing matchMedia) → fully static/deterministic.
 *
 * Below the briefing, a live chat layers on the SAME persisted Brok thread via
 * useBrokChat({ kickoff:false }) — only the DELTA appended after the briefing opened
 * is shown inline (baseline-slice); the full history lives at /brok, reached by the
 * «Ganze Unterhaltung öffnen» link. BrokRay is the working indicator.
 *
 * [Source: brokly-design-reference/components/brokly/heute-v2-view.tsx]
 * [Source: docs/stories/ui-u10-brok-briefing-ray.md AC2/AC3]
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowRight, ListPlus, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '../../../components/ui/Badge';
import { InsightCard } from '../../entity-detail/components/InsightCard';
import { cn } from '../../../lib/cn';
import { isLiveData } from '../../../lib/data-mode';
import { loadBrokerSession } from '../../../lib/auth/auth-session';
import { BrokRay, brokRayMotionAllowed } from '../../brok/components/BrokRay';
import { useBrokChat } from '../../brok/hooks/useBrokChat';
import { BrokMessageList } from '../../brok/components/BrokMessageList';
import { BrokComposer } from '../../brok/components/BrokComposer';
import { SuggestionChips } from '../../brok/components/SuggestionChips';
import {
  greeting,
  vornameFromDisplayName,
  decisionSentence,
  briefingDecisionCount,
  BRIEFING_OPEN_FULL_LABEL,
  BROK_INPUT_PLACEHOLDER,
} from '../../brok/welcome';
import { useBriefing } from '../hooks/useBriefing';
import { BriefingDecisionTiles } from './BriefingDecisionTiles';
import { HEUTE_RECOMMENDATIONS } from '../heute-mock';
import { BRIEFING_KPIS } from '../briefing-mock';

/** Number of briefing sections that stagger in. */
const BRIEFING_SECTIONS = 4;

/**
 * Module-level: the entrance stagger plays only ONCE per session (page lifetime).
 * Toggling Dashboard↔Briefing again shows the briefing fully at once.
 */
let hasAnimatedBriefingSession = false;

/** One briefing message: a BrokRay avatar + a left-aligned card bubble. */
function BriefingBubble({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-selected">
        <BrokRay size={18} label="Brok" />
      </span>
      <div className="min-w-0 flex-1 rounded-md rounded-tl-sm border bg-card p-4">{children}</div>
    </div>
  );
}

export function BriefingView() {
  const live = isLiveData();
  const summary = useBriefing();
  const vorname = vornameFromDisplayName(loadBrokerSession()?.displayName);
  const decisionCount = briefingDecisionCount(summary);

  // Endpoint-less placeholders: mock arrays in mock mode, EMPTY in live mode (honesty).
  const recommendations = live ? [] : HEUTE_RECOMMENDATIONS;
  const kpis = live ? [] : BRIEFING_KPIS;

  // Once-per-session entrance stagger, gated by prefers-reduced-motion / jsdom.
  const [staticAll] = useState(() => hasAnimatedBriefingSession || !brokRayMotionAllowed());
  const [visible, setVisible] = useState(staticAll ? BRIEFING_SECTIONS : 0);
  useEffect(() => {
    if (staticAll) {
      hasAnimatedBriefingSession = true;
      return;
    }
    let n = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const tick = () => {
      n += 1;
      setVisible(n);
      if (n < BRIEFING_SECTIONS) {
        timers.push(setTimeout(tick, 350));
      } else {
        hasAnimatedBriefingSession = true;
      }
    };
    timers.push(setTimeout(tick, 350));
    return () => timers.forEach(clearTimeout);
  }, [staticAll]);

  // Chat on the SAME persisted thread, kickoff suppressed; baseline-slice delta.
  const { messages, streaming, ready, sendMessage, confirm, reject, rueckfrage } = useBrokChat({
    kickoff: false,
  });
  const baselineRef = useRef<number | null>(null);
  if (ready && baselineRef.current === null) baselineRef.current = messages.length;
  const delta = baselineRef.current === null ? [] : messages.slice(baselineRef.current);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      {/* 1 — Greeting + decision sentence */}
      {visible >= 1 && (
        <BriefingBubble>
          <p className="text-sm leading-relaxed text-foreground">
            <span className="font-semibold">{greeting(vorname)}</span>{' '}
            {decisionSentence(decisionCount)}
          </p>
        </BriefingBubble>
      )}

      {/* 2 — Entscheid tiles */}
      {visible >= 2 && decisionCount !== 0 && (
        <BriefingBubble>
          <p className="text-sm leading-relaxed text-foreground">
            Das braucht heute deinen Entscheid:
          </p>
          <div className="mt-3">
            <BriefingDecisionTiles summary={summary} />
          </div>
        </BriefingBubble>
      )}

      {/* 3 — KI-Empfehlungen (documented placeholder) */}
      {visible >= 3 && (
        <BriefingBubble>
          <p className="text-sm leading-relaxed text-foreground">Meine Empfehlungen für dich:</p>
          {recommendations.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Keine KI-Empfehlungen.</p>
          ) : (
            <div className="mt-3 grid grid-cols-1 gap-3">
              {recommendations.map((r, i) => (
                <InsightCard
                  key={i}
                  title={r.kind}
                  tag={<Badge variant={r.tone}>{r.tag}</Badge>}
                  konfidenz={r.konfidenz}
                  actions={[
                    { action: 'task', label: 'Aufgabe erstellen', icon: ListPlus, variant: 'outline' },
                    { action: 'dismiss', label: 'Verwerfen', icon: X, variant: 'ghost', pushEnd: true },
                  ]}
                  onAction={() => {}}
                >
                  {r.text}
                </InsightCard>
              ))}
            </div>
          )}
        </BriefingBubble>
      )}

      {/* 4 — Portfolio-mini KPIs (documented placeholder) */}
      {visible >= 4 && (
        <BriefingBubble>
          <p className="text-sm leading-relaxed text-foreground">Dein Portfolio in Zahlen:</p>
          {kpis.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Noch keine Kennzahlen.</p>
          ) : (
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {kpis.map((k) => (
                <div
                  key={k.label}
                  className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3"
                >
                  <span className="text-xs text-muted-foreground">{k.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="mono-num text-lg font-semibold text-foreground">{k.value}</span>
                    {k.delta && <Badge variant={k.deltaTone}>{k.delta}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </BriefingBubble>
      )}

      {/* Chat delta (only what was appended after the briefing opened) */}
      {(delta.length > 0 || streaming) && (
        <BrokMessageList
          messages={delta}
          streaming={streaming}
          onConfirm={confirm}
          onReject={(id, note) => void reject(id, note ?? '')}
          onRueckfrage={rueckfrage}
        />
      )}

      {/* Composer + suggestions + full-thread link */}
      <div>
        <SuggestionChips onPick={(label) => void sendMessage(label)} disabled={streaming} />
        <BrokComposer
          onSend={(text, attachmentId, attachmentName) =>
            void sendMessage(text, { attachmentId, attachmentName })
          }
          disabled={streaming}
          placeholder={BROK_INPUT_PLACEHOLDER}
        />
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <BrokRay animated={streaming} size={16} label={streaming ? 'Brok arbeitet' : undefined} />
            Brok kann Fehler machen, Angaben prüfen.
          </span>
          <Link
            to="/brok"
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground',
            )}
          >
            {BRIEFING_OPEN_FULL_LABEL}
            <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
